import { Card, CardContent, Spinner, TenantProvider } from '@lensora/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { ResendConfirmationButton } from '@/components/auth/ResendConfirmationButton'
import { OnboardingChat, type OnboardingStatus } from '@/components/onboarding/OnboardingChat'
import { Sidebar } from '@/components/sidebar'
import { useMe } from '@/hooks/use-me'
import { useTenantConfig } from '@/hooks/use-tenant-config'
import { api } from '@/lib/api'
import { fetchBillingStatus } from '@/lib/billing'
import { BillingPage } from '@/routes/dashboard/billing/page'

// The backend deliberately leaves `email_confirm: true` on the Supabase
// account so an unconfirmed self-serve user can still sign in — meaning
// every unconfirmed signup lands here, not just an edge case. This panel
// must be checked before needsSubscription or all of them see the Stripe
// paywall instead of a confirm-your-email prompt.
function EmailConfirmationPanel({ email }: { email: string }) {
  return (
    <div className="mx-auto max-w-sm pt-16">
      <Card>
        <CardContent className="pt-6 text-center space-y-4">
          <h2 className="text-lg font-semibold text-text">
            Confirm your email to activate your 25 credits
          </h2>
          <p className="text-sm text-text-muted">
            We sent a confirmation link to your inbox. Click it to unlock your free trial credits
            and start using Lensora.
          </p>
          <ResendConfirmationButton email={email} />
        </CardContent>
      </Card>
    </div>
  )
}

export function DashboardLayout() {
  const { data: tenantConfig } = useTenantConfig()
  const { data: me, isLoading: meLoading } = useMe()
  const queryClient = useQueryClient()
  const [onboardingDone, setOnboardingDone] = React.useState(false)

  // Self-serve customers (client-role users in the shared app tenant) need
  // either a subscription (Stripe trial counts) or unspent signup trial
  // credits before using the product
  const isSelfServeClient = me?.user.role === 'client' && me?.tenant?.slug === 'app'

  const { data: billingStatus, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    enabled: isSelfServeClient,
    staleTime: 30_000,
  })

  const { data: onboarding, isLoading: onboardingLoading } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.get<OnboardingStatus>('/api/onboarding/status'),
    enabled: !!me,
    staleTime: 30_000,
  })

  const firstName = React.useMemo(() => {
    const full = me?.user.fullName ?? ''
    if (!full.trim() || full.includes('@')) return 'there'
    return full.trim().split(' ')[0] ?? 'there'
  }, [me])

  const isInitialLoading = meLoading || onboardingLoading || (isSelfServeClient && billingLoading)
  // Checked before needsSubscription: an unconfirmed self-serve account has
  // no subscription and no trial credits yet either, so without this branch
  // it would also satisfy needsSubscription below and show the Stripe
  // paywall instead of a confirm-your-email prompt.
  const needsEmailConfirmation = isSelfServeClient && !!billingStatus?.trialGrantPending
  // The paywall only appears once the signup trial credits are spent, so a
  // brand-new account goes straight into onboarding instead of Checkout.
  const needsSubscription =
    isSelfServeClient &&
    !!billingStatus &&
    !billingStatus.hasSubscription &&
    !billingStatus.onTrialCredits &&
    !needsEmailConfirmation
  const shouldShowOnboarding = !onboardingDone && !!onboarding && !onboarding.onboarding_completed

  return (
    <TenantProvider config={tenantConfig}>
      <div className="flex h-screen overflow-hidden bg-surface">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto p-6">
            {isInitialLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            ) : needsEmailConfirmation ? (
              <EmailConfirmationPanel email={me!.user.email} />
            ) : needsSubscription ? (
              // Soft paywall: the billing page doubles as the plan picker and
              // handles resume-after-checkout polling
              <BillingPage />
            ) : shouldShowOnboarding && onboarding ? (
              <OnboardingChat
                firstName={firstName}
                status={onboarding}
                onComplete={() => {
                  setOnboardingDone(true)
                  void queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
                  void queryClient.invalidateQueries({ queryKey: ['websites'] })
                }}
              />
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </TenantProvider>
  )
}
